/**
 * Settings -> Notifications -> Email delivery: a company's OWN outgoing mail server (per-company SMTP).
 *
 * Without a configured, verified, enabled row a company's mail goes through the platform relay exactly
 * as it always has. Account-security mail (password reset, email verification) ALWAYS uses the
 * platform relay, so a broken company server can never lock anyone out.
 *
 * THE PASSWORD IS WRITE-ONLY. It is accepted on `PUT`, encrypted server-side, and never appears in any
 * response, log or audit diff — responses carry only `passwordSet`. Omit `password` on a later `PUT` to
 * keep the stored one.
 *
 * ENABLING IS GATED ON A TEST. Changing host, port, username, password or the from address clears
 * `verifiedAt` and switches the row off; it can only be switched on again after `POST /test` succeeds
 * against the current settings. A test email goes only to the address of the signed-in caller.
 *
 * Kept in its own contract (and route file) so it does not share a file with the rules/audience routes.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { routeMeta } from './meta.js';
import { emailSchema, errorBodySchema, instantSchema, orgIdSchema } from '../schemas/common.js';

const c = initContract();

/** Ports a customer relay may use. Everything else is rejected before any connection is attempted. */
export const ORG_SMTP_ALLOWED_PORTS = [25, 465, 587, 2525] as const;

export const orgEmailSettingsSchema = z.object({
  orgId: orgIdSchema,
  /** Whether the company relay is in use. Only ever true after a successful test. */
  enabled: z.boolean(),
  host: z.string().nullable(),
  port: z.number().int().nullable(),
  username: z.string().nullable(),
  /** True once a password is stored. The password itself is never returned. */
  passwordSet: z.boolean(),
  fromName: z.string().nullable(),
  fromAddress: z.string().nullable(),
  replyTo: z.string().nullable(),
  fallbackToPlatform: z.boolean(),
  verifiedAt: instantSchema.nullable(),
  lastTestAt: instantSchema.nullable(),
  lastTestStatus: z.enum(['success', 'failed']).nullable(),
  lastTestError: z.string().nullable(),
  updatedAt: instantSchema.nullable(),
});
export type OrgEmailSettings = z.infer<typeof orgEmailSettingsSchema>;

export const updateOrgEmailSettingsBodySchema = z.object({
  host: z.string().trim().min(1).max(255),
  port: z
    .number()
    .int()
    .refine((p) => (ORG_SMTP_ALLOWED_PORTS as readonly number[]).includes(p), {
      message: `Port must be one of ${ORG_SMTP_ALLOWED_PORTS.join(', ')}`,
    }),
  username: z.string().trim().min(1).max(255),
  /** Write-only. Omit to keep the stored password; required the first time settings are saved. */
  password: z.string().min(1).max(500).optional(),
  fromName: z.string().trim().max(200).nullable().optional(),
  fromAddress: emailSchema,
  replyTo: emailSchema.nullable().optional(),
  fallbackToPlatform: z.boolean().default(false),
  /** Rejected (400) unless the current settings have passed a test. */
  enabled: z.boolean(),
});
export type UpdateOrgEmailSettingsBody = z.infer<typeof updateOrgEmailSettingsBodySchema>;

export const orgEmailTestResultSchema = z.object({
  status: z.enum(['success', 'failed']),
  /** A safe, human-readable reason on failure — never a stack trace, never a credential. */
  error: z.string().nullable(),
  testedAt: instantSchema,
  /** The address the test email was sent to (the caller's own). */
  sentTo: z.string(),
});
export type OrgEmailTestResult = z.infer<typeof orgEmailTestResultSchema>;

export const emailSettingsContract = c.router(
  {
    get: {
      method: 'GET',
      path: '/',
      responses: {
        200: orgEmailSettingsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: "The company's own SMTP settings (password never returned)",
      metadata: routeMeta(['notification.config:read', 'notification.config:manage']),
    },
    update: {
      method: 'PUT',
      path: '/',
      body: updateOrgEmailSettingsBodySchema,
      responses: {
        200: orgEmailSettingsSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** The server-address safety check refused the host (private/internal address). */
        422: errorBodySchema,
      },
      summary:
        'Save the company SMTP settings; enabling requires a successful test of these settings',
      metadata: routeMeta('notification.config:manage'),
    },
    test: {
      method: 'POST',
      path: '/test',
      body: z.object({}).strict(),
      responses: {
        200: orgEmailTestResultSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
        429: errorBodySchema,
      },
      summary: "Send a test email through the saved settings, to the caller's own address only",
      metadata: routeMeta('notification.config:manage'),
    },
  },
  { pathPrefix: '/notification-settings/email' },
);
